import { Button } from "../ui/button";
import { MessageCircle, RefreshCw, User, Coins } from "lucide-react";
import ChatInput from "../ChatInput";
import MessageContent from "../MessageContent";

/**
 * Main chat pane for ErosCoach — empty states, messages list, loading
 * indicator, ChatInput. Extracted from `pages/ErosCoach.jsx` as part of
 * the P1 #2 refactor (pure code-move, zero behaviour change).
 *
 * All state lives in the parent `ErosCoach`. Handlers passed via props.
 */
export default function CoachChatPane({
  // State refs
  chatContainerRef,
  messagesEndRef,
  chatInputRef,
  // Messages + loading
  messages,
  loading,
  // Empty-state flags / data
  pendingMatchSelection,
  selectedMatch,
  sessions,
  matches,
  // Coin balance (for needsCoins pill computation)
  coinBalance,
  setShowCoinPurchaseModal,
  goToTopupPage,
  // Handlers
  loadSession,
  createNewSession,
  sendMessage,
  retryMessage,
  // User / persona avatars
  currentSessionId,
  userPersona,
}) {
  return (
    <main className="flex-1 flex flex-col min-w-0">
      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6"
      >
        {messages.length === 0 ? (
          pendingMatchSelection && selectedMatch ? (
            // User has just switched match via dropdown — explicit picker
            // empty-state. Removes the previous race where the chat
            // window kept the prior match's session and the wizard /
            // digest fired against a stale session_id.
            (() => {
              const matchSessions = sessions.filter(s => s.match_id === selectedMatch);
              const matchDoc = matches.find(m => m.match_id === selectedMatch);
              const matchFirstName = (matchDoc?.name || "this match").split(/\s+/)[0];
              const latest = matchSessions[0];
              return (
                <div className="h-full flex items-center justify-center" data-testid="match-switch-empty-state">
                  <div className="text-center max-w-md space-y-4">
                    <MessageCircle className="w-12 h-12 mx-auto text-divine-gold/40" />
                    <div>
                      <h2 className="text-white/80 text-lg font-cinzel mb-1">
                        Coaching for {matchFirstName}
                      </h2>
                      <p className="text-white/50 text-sm">
                        Continue a previous coaching session from the list, or start a new one.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                      {latest && (
                        <Button
                          onClick={() => loadSession(latest.session_id)}
                          className="bg-white/10 hover:bg-white/20 text-white border border-divine-gold/20"
                          data-testid="empty-state-continue-last"
                        >
                          Continue last session
                        </Button>
                      )}
                      <Button
                        onClick={createNewSession}
                        className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium"
                        data-testid="empty-state-new-session"
                      >
                        New session
                      </Button>
                    </div>
                    {matchSessions.length > 0 && (
                      <p className="text-white/30 text-xs pt-2">
                        {matchSessions.length} previous session{matchSessions.length === 1 ? "" : "s"} with {matchFirstName} — see the list below
                      </p>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-divine-gold/30" />
                <h2 className="text-white/60 text-lg mb-2 font-cinzel">Agent Eros Advice</h2>
                <p className="text-white/40 text-sm max-w-md">
                  Your emotionally-intelligent relationship coach.
                  Ask for advice, guidance, or help with your relationships.
                </p>
              </div>
            </div>
          )
        ) : (
          messages.map((msg, idx) => {
            const isFailed = msg.status === "failed";
            const needsCoins = msg.status === "needs_coins";
            const coinsToppedUp =
              needsCoins &&
              coinBalance !== null &&
              msg.coinBalanceAtFailure !== undefined &&
              coinBalance > msg.coinBalanceAtFailure;
            const showFailedStyling = isFailed || needsCoins;
            return (
              <div
                key={msg.client_id || idx}
                className={`flex gap-3 sm:gap-4 ${msg.role === "user" ? "sm:justify-end" : "sm:justify-start"}`}
              >
                {/* Assistant Avatar - Hidden on mobile */}
                {msg.role === "assistant" && (
                  <div className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0">
                    <img
                      src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                      alt="Eros"
                      className="w-8 h-8 object-contain"
                      style={{ animation: "eros-glow-gold 3s ease-in-out infinite" }}
                    />
                  </div>
                )}

                {/* Message Bubble - Full width on mobile */}
                <div className="w-full sm:max-w-[70%]">
                  <div className={`rounded-2xl px-4 sm:px-5 py-3 transition-opacity ${
                    msg.role === "user"
                      ? "bg-emerald-500/40 border border-emerald-500/30 text-white sm:inline-block sm:float-right"
                      : "glass-gold text-white sm:inline-block"
                  } ${showFailedStyling ? "opacity-60 border-l-2 border-l-rose-400" : ""}`}>
                    {/* Show image if present in user message */}
                    {msg.image?.preview && (
                      <div className="mb-2">
                        <img
                          src={msg.image.preview}
                          alt="Attached"
                          className="max-w-full max-h-64 rounded-lg"
                        />
                      </div>
                    )}
                    <MessageContent
                      content={msg.content}
                      matchPersonaId={selectedMatch}
                      sessionId={currentSessionId}
                    />
                  </div>
                  {/* Failure / coin-needed pill — sits beneath the bubble */}
                  {(isFailed || needsCoins) && (
                    <div className={`mt-1 ${msg.role === "user" ? "sm:text-right" : "sm:text-left"} clear-both`}>
                      {isFailed && (
                        <button
                          type="button"
                          onClick={() => retryMessage(msg.client_id)}
                          disabled={loading}
                          data-testid={`retry-failed-${msg.client_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 hover:bg-rose-500/20 hover:text-rose-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Failed to send — Click to retry
                        </button>
                      )}
                      {needsCoins && !coinsToppedUp && (
                        <button
                          type="button"
                          onClick={() => (goToTopupPage ? goToTopupPage() : setShowCoinPurchaseModal(true))}
                          data-testid={`purchase-coins-${msg.client_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 hover:text-amber-100 transition-colors"
                        >
                          <Coins className="w-3 h-3" />
                          Purchase Coins
                        </button>
                      )}
                      {needsCoins && coinsToppedUp && (
                        <button
                          type="button"
                          onClick={() => retryMessage(msg.client_id)}
                          disabled={loading}
                          data-testid={`retry-after-topup-${msg.client_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:text-emerald-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Click to retry
                        </button>
                      )}
                    </div>
                  )}
                  <div className="clear-both" />
                </div>

                {/* User Avatar - Hidden on mobile */}
                {msg.role === "user" && (
                  <div className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 overflow-hidden bg-divine-red/20">
                    {userPersona?.thumbnail_url || userPersona?.picture_url || userPersona?.picture ? (
                      <img
                        src={userPersona.thumbnail_url || userPersona.picture_url || userPersona.picture}
                        alt={userPersona.name}
                        className="w-full h-full object-cover"
                      />
                    ) : userPersona?.name ? (
                      <span className="font-cinzel text-sm text-divine-gold">
                        {userPersona.name[0]}
                      </span>
                    ) : (
                      <User className="w-5 h-5 text-divine-red" />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex gap-3 sm:gap-4 sm:justify-start">
            <div className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                alt="Eros"
                className="w-8 h-8 object-contain animate-pulse"
                style={{ animation: "eros-glow-gold 1.5s ease-in-out infinite" }}
              />
            </div>
            <div className="w-full sm:w-auto rounded-2xl px-4 sm:px-5 py-3 glass-gold">
              <div className="flex gap-2">
                <div className="w-2 h-2 rounded-full animate-bounce bg-divine-gold" />
                <div className="w-2 h-2 rounded-full animate-bounce bg-divine-gold" style={{ animationDelay: "0.1s" }} />
                <div className="w-2 h-2 rounded-full animate-bounce bg-divine-gold" style={{ animationDelay: "0.2s" }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="sticky bottom-0 border-t border-divine-gold/20 bg-divine-black-light/95 backdrop-blur-xl z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <ChatInput
            ref={chatInputRef}
            onSend={sendMessage}
            loading={loading}
            placeholder="Ask Agent Eros for advice..."
            testId="eroscoach-input"
            enableImageAttachment={true}
            sessionId={currentSessionId}
          />
        </div>
      </div>
    </main>
  );
}
